import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import LanguageIcon from '@mui/icons-material/Language';
import { LANGUAGES, LANGUAGE_CODES, DEFAULT_LANGUAGE } from '../../constants/languages';
import { setLanguage, getActiveLanguage } from '../../i18n';
import { COLORS, FONTS } from '../../theme/tokens';

/**
 * Storefront language dropdown.
 * @param {object} props
 * @param {boolean} [props.enabled=true] master switcher on/off from admin config
 * @param {string[]} [props.languagesInSwitcher] codes to list; defaults to all 10
 * @param {'header'|'banner'|'drawer'} [props.variant='header'] visual density
 */
const LanguageSwitcher = ({
  enabled = true,
  languagesInSwitcher = LANGUAGE_CODES,
  variant = 'header',
}) => {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!enabled) return null;

  const allowed = new Set(
    (languagesInSwitcher || []).filter((c) => LANGUAGE_CODES.includes(c))
  );
  // Always allow EN as selectable if list empty
  if (allowed.size === 0) allowed.add(DEFAULT_LANGUAGE);

  const options = LANGUAGES.filter((lang) => allowed.has(lang.code));
  const active = getActiveLanguage();
  const displayCode = allowed.has(active) ? active : DEFAULT_LANGUAGE;

  const isBanner = variant === 'banner';
  const isDrawer = variant === 'drawer';

  const handleSelect = (code) => {
    setLanguage(code);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        aria-label="select language"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<LanguageIcon sx={{ fontSize: isBanner ? 16 : 18 }} />}
        size="small"
        sx={{
          color: isBanner ? '#fff' : COLORS.textPrimary,
          minWidth: 0,
          px: isDrawer ? 1.5 : 1,
          py: isBanner ? 0 : 0.5,
          fontFamily: FONTS.body,
          fontSize: isBanner ? '0.75rem' : '0.8125rem',
          fontWeight: 600,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          '&:hover': {
            bgcolor: isBanner ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)',
          },
        }}
      >
        {displayCode}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={lang.code === (i18n.language || '').toUpperCase().slice(0, 2)}
            onClick={() => handleSelect(lang.code)}
          >
            <ListItemText primary={lang.nativeName} secondary={lang.code} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
